import { clampTo, extractImageFrames } from '../../tools';
import type { atlascloudModelConfig } from '../types';

export const googleConfigs: Record<string, atlascloudModelConfig> = {
    'gemini-omni-flash': {
        // https://www.atlascloud.ai/models/google/gemini-omni-flash/text-to-video-developer
        // https://www.atlascloud.ai/models/google/gemini-omni-flash/image-to-video-developer
        // https://www.atlascloud.ai/models/google/gemini-omni-flash/reference-to-video-developer
        apiPath: '/api/v1/model/generateVideo',
        pollApiPath: '/api/v1/model/prediction/${requestId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage] = extractImageFrames(imagePrompt);
            if (frameMode === 'first') {
                return {
                    model: 'google/gemini-omni-flash/image-to-video-developer',
                    prompt: prompt.slice(0, 20000),
                    duration: clampTo(duration || 8, [4, 6, 8, 10] as const, 8),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    seed: -1,
                    // 首帧 URL
                    images: [firstImage!],
                };
            } else if (frameMode === 'ref') {
                return {
                    model: 'google/gemini-omni-flash/image-to-video-developer',
                    prompt: prompt.slice(0, 20000),
                    duration: clampTo(duration || 8, [4, 6, 8, 10] as const, 8),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    seed: -1,
                    // 图片 URL
                    images: imagePrompt!.slice(0, 7),
                };
            } else if (frameMode === 'v2v') {
                const ends = Math.min(10, Math.max(1, Math.floor((providerOptions as Record<string, any> | undefined)?._media?.videoDurationSeconds ?? 10)));
                return {
                    model: 'google/gemini-omni-flash/reference-to-video-developer',
                    prompt: prompt.slice(0, 20000),
                    duration: clampTo(duration || 8, [4, 6, 8, 10] as const, 8),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    seed: -1,
                    // 图片 URL
                    ...(imagePrompt?.length ? { images: imagePrompt.slice(0, 5) } : {}),
                    // 视频片段
                    video_clips: [{ url: videoUri, start: 0, ends }],
                };
            } else {
                return {
                    model: 'google/gemini-omni-flash/text-to-video-developer',
                    prompt: prompt.slice(0, 20000),
                    duration: clampTo(duration || 8, [4, 6, 8, 10] as const, 8),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    resolution: clampTo(quality || '720p', ['720p', '1080p', '4k'] as const, '720p'),
                    seed: -1,
                };
            }
        },
    },
    'gemini-omni-flash-api': {
        // https://www.atlascloud.ai/models/google/gemini-omni-flash/text-to-video
        // https://www.atlascloud.ai/models/google/gemini-omni-flash/image-to-video
        // https://www.atlascloud.ai/models/google/gemini-omni-flash/reference-to-video
        // https://www.atlascloud.ai/models/google/gemini-omni-flash/video-edit
        apiPath: '/api/v1/model/generateVideo',
        pollApiPath: '/api/v1/model/prediction/${requestId}',
        enableTranslation: false,
        buildInput: (params) => {
            const { prompt, imagePrompt, videoUri, aspectRatio, duration, quality, modelUi, providerOptions } = params;
            const frameMode = modelUi?.frameMode;
            const [firstImage] = extractImageFrames(imagePrompt);
            if (frameMode === 'first') {
                return {
                    model: 'google/gemini-omni-flash/image-to-video',
                    prompt: prompt.slice(0, 20000),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 10, [3, 4, 5, 6, 7, 8, 9, 10] as const, 10),
                    resolution: clampTo(quality || '720p', ['720p'] as const, '720p'),
                    seed: -1,
                    // 首帧 URL
                    image: firstImage,
                    // 可选参数：开启时传 high，关闭不传
                    ...((providerOptions as Record<string, any> | undefined)?.thinking_level === 'high' ? { thinking_level: 'high' } : {}),
                };
            } else if (frameMode === 'ref') {
                return {
                    model: 'google/gemini-omni-flash/reference-to-video',
                    prompt: prompt.slice(0, 20000),
                    aspect_ratio: clampTo(aspectRatio || '16:9', ['16:9', '9:16'] as const, '16:9'),
                    duration: clampTo(duration || 10, [3, 4, 5, 6, 7, 8, 9, 10] as const, 10),
                    resolution: clampTo(quality || '720p', ['720p'] as const, '720p'),
                    seed: -1,
                    // 图片 URL
                    images: imagePrompt!.slice(0, 10),
                    // 可选参数：开启时传 high，关闭不传
                    ...((providerOptions as Record<string, any> | undefined)?.thinking_level === 'high' ? { thinking_level: 'high' } : {}),
                };
            } else if (frameMode === 'v2v') {
                return {
                    model: 'google/gemini-omni-flash/video-edit',
                    prompt: prompt.slice(0, 20000),
                    resolution: clampTo(quality || '720p', ['720p'] as const, '720p'),
                    seed: -1,
                    // 图片 URL
                    ...(imagePrompt?.length ? { images: imagePrompt.slice(0, 5) } : {}),
                    // 视频 URL
                    video: videoUri,
                    // 可选参数：开启时传 high，关闭不传
                    ...((providerOptions as Record<string, any> | undefined)?.thinking_level === 'high' ? { thinking_level: 'high' } : {}),
                };
            } else {
                return {
                    model: 'google/gemini-omni-flash/text-to-video',
                    prompt: prompt.slice(0, 20000),
                    duration: clampTo(duration || 10, [3, 4, 5, 6, 7, 8, 9, 10] as const, 10),
                    resolution: clampTo(quality || '720p', ['720p'] as const, '720p'),
                    seed: -1,
                    // 可选参数：开启时传 high，关闭不传
                    ...((providerOptions as Record<string, any> | undefined)?.thinking_level === 'high' ? { thinking_level: 'high' } : {}),
                };
            }
        },
    },
};
